<template>
  <div class="container-tabs">
    <div
      v-for="(item, index) in data"
      :key="item.shipmentContainerCode"
      class="container-tabs__card"
      :class="{ 'is-active': item.shipmentContainerCode === activeCode }"
      @click="selectContainer(item.shipmentContainerCode)"
    >
      <div class="container-tabs__card-top">
        <span class="container-tabs__badge">{{
          item.containerName || '-'
        }}</span>
        <span v-if="item.shipmentBookingCode" class="container-tabs__locked">
          <i class="el-icon-lock" />
          {{ $l('label_booked') || '已订舱' }}
        </span>
        <div class="container-tabs__actions">
          <span
            class="container-tabs__action"
            :class="{
              'is-disabled': index === 0 || !!item.shipmentBookingCode,
            }"
            :title="$l('tips_move_left') || '左移'"
            @click.stop="reorderContainer(index, -1)"
          >
            <i class="el-icon-arrow-left" />
          </span>
          <span
            class="container-tabs__action"
            :class="{
              'is-disabled':
                index === data.length - 1 || !!item.shipmentBookingCode,
            }"
            :title="$l('tips_move_right') || '右移'"
            @click.stop="reorderContainer(index, 1)"
          >
            <i class="el-icon-arrow-right" />
          </span>
          <span
            class="container-tabs__action"
            :class="{ 'is-disabled': !!item.shipmentBookingCode }"
            :title="$l('btn_edit') || '编辑'"
            @click.stop="openEditContainer(item)"
          >
            <i class="el-icon-edit" />
          </span>
          <span
            class="container-tabs__action"
            :title="$l('btn_copy') || '复制'"
            @click.stop="copyContainer(item)"
          >
            <i class="el-icon-copy-document" />
          </span>
          <span
            v-if="data.length > 1"
            class="container-tabs__action is-danger"
            :class="{ 'is-disabled': !!item.shipmentBookingCode }"
            :title="$l('INDEX.btn_delete') || '删除'"
            @click.stop="deleteContainer(item)"
          >
            <i class="el-icon-close" />
          </span>
        </div>
      </div>
      <div class="container-tabs__no">{{ item.containerNo || '-' }}</div>
      <div class="container-tabs__gauges">
        <div class="container-tabs__gauge-track">
          <div
            class="container-tabs__gauge-fill"
            :style="{
              width:
                Math.min(Number(item.volumeUsageRate || 0) * 100, 100) + '%',
            }"
          />
        </div>
        <div class="container-tabs__gauge-track">
          <div
            class="container-tabs__gauge-fill is-weight"
            :style="{
              width:
                Math.min(Number(item.weightUsageRate || 0) * 100, 100) + '%',
            }"
          />
        </div>
      </div>
      <div class="container-tabs__stats">
        <span>{{ (item.listGoods || []).length }} SKU</span>
        <span>{{ (Number(item.volumeUsageRate || 0) * 100).toFixed(0) }}%</span>
      </div>
    </div>

    <div
      class="container-tabs__add"
      :title="$l('btn_add_container') || '新增货柜'"
      @click="openCreateContainer"
    >
      <i class="el-icon-plus" />
    </div>

    <EditContainer
      :visible.sync="editVisible"
      :shipmentContainerCode="currentShipmentContainerCode"
      :shipmentCode="shipmentCode"
      @reload="handleContainerReload"
    />
  </div>
</template>

<script>
import EditLoading from '@/mixins/edit-loading'
import EditContainer from '../edit-container/index.vue'

export default {
  components: { EditContainer },
  mixins: [EditLoading],
  props: {
    data: { type: Array, default: () => [] },
    activeCode: { type: String, default: '' },
    shipmentCode: { type: String, default: '' },
  },
  data() {
    return {
      editVisible: false,
      currentShipmentContainerCode: '',
    }
  },
  methods: {
    selectContainer(code) {
      this.$emit('change', code)
    },
    openCreateContainer() {
      this.currentShipmentContainerCode = ''
      this.editVisible = true
    },
    openEditContainer(item) {
      this.selectContainer(item.shipmentContainerCode)
      if (item.shipmentBookingCode) {
        this.$message.warning(
          this.$l('tips_container_booked_no_edit') ||
            '该货柜已关联订舱，无法编辑'
        )
        return
      }
      this.currentShipmentContainerCode = item.shipmentContainerCode
      this.editVisible = true
    },
    handleContainerReload() {
      this.$emit('reload')
    },
    async copyContainer(item) {
      this.selectContainer(item.shipmentContainerCode)

      const confirm = await this.$confirm(
        `${this.$l('tips_confirm_copy_container_prefix') ||
          '确认复制货柜「'}${item.containerName || ''} ${item.containerNo ||
          ''}${this.$l('tips_confirm_copy_container_suffix') || '」吗？'}`,
        this.$l('tips_confirm_title') || '提示',
        { type: 'info' }
      ).catch(() => null)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerSaveApi({
          shipmentCode: this.shipmentCode,
          containerCode: item.containerCode,
          containerName: item.containerName,
          containerNo: '',
          sealNo: '',
          extendParameterType: item.extendParameterType,
          extendParameterLength: item.extendParameterLength,
          extendParameterWidth: item.extendParameterWidth,
          extendParameterHeight: item.extendParameterHeight,
          containerBookingVolume: item.containerBookingVolume,
          containerWeight: item.containerWeight,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_copy_ok') || '复制成功',
      })
      this.$emit('reload')
    },
    async deleteContainer(item) {
      this.selectContainer(item.shipmentContainerCode)
      if (item.shipmentBookingCode) {
        this.$message.warning(
          this.$l('tips_container_booked_no_delete') ||
            '该货柜已关联订舱，无法删除'
        )
        return
      }

      const confirm = await this.$confirm(
        this.$l('tips_confirm_delete_container') || '确认删除该货柜吗？',
        this.$l('tips_confirm_title') || '提示',
        {
          type: 'warning',
        }
      ).catch(() => null)
      if (!confirm) return

      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerDelApi({
          shipmentContainerCode: item.shipmentContainerCode,
          shipmentCode: this.shipmentCode,
        })
        .catch(() => null)
      this.loadingClose()
      if (!res) return

      this.$message({
        type: 'success',
        message: res.msg || this.$l('message_delete_ok') || '删除成功',
      })
      this.$emit('reload')
    },
    async reorderContainer(index, direction) {
      const item = this.data[index]
      if (!item) return
      this.selectContainer(item.shipmentContainerCode)

      if (item.shipmentBookingCode) {
        this.$message.warning(
          this.$l('tips_container_booked_no_reorder') ||
            '该货柜已关联订舱，无法调整排序'
        )
        return
      }

      const newIndex = index + direction
      if (newIndex < 0 || newIndex >= this.data.length) return

      const target = this.data[newIndex]
      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomShipmentContainerChangeSortOrderApi({
          shipmentCode: this.shipmentCode,
          shipmentContainerCode: item.shipmentContainerCode,
          targetContainerCode: target.shipmentContainerCode,
        })
        .catch(() => null)
      this.loadingClose()

      if (!res) {
        this.$message({
          type: 'error',
          message: this.$l('message_reorder_failed') || '排序失败，请重试',
        })
        return
      }

      this.$emit('reload')
    },
  },
}
</script>

<style lang="scss" scoped>
.container-tabs {
  display: flex;
  gap: 8px;
  padding: 0 2px 10px 0;
  margin-bottom: 6px;
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: #cfd8e6 transparent;
}

.container-tabs::-webkit-scrollbar {
  height: 6px;
}

.container-tabs::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 999px;
}

.container-tabs::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.45);
  border-radius: 999px;
  border: 1px solid transparent;
  background-clip: padding-box;
}

.container-tabs::-webkit-scrollbar-thumb:hover {
  background: rgba(100, 116, 139, 0.52);
  background-clip: padding-box;
}

.container-tabs__card {
  flex: 0 0 122px;
  background: #fff;
  border: 1px solid #d9e6f5;
  border-radius: 10px;
  padding: 7px 10px 8px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  min-height: 82px;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  }

  &.is-active {
    border-color: #409eff;
    box-shadow: 0 0 0 1px #409eff, 0 2px 8px rgba(64, 158, 255, 0.1);
    background: #f7fbff;

    .container-tabs__badge {
      background: #edf5ff;
      color: #409eff;
    }
  }
}

.container-tabs__card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 6px;
  gap: 4px;
}

.container-tabs__badge {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 0 4px;
  border-radius: 4px;
  letter-spacing: 0.04em;
  white-space: nowrap;
  line-height: 16px;
}

.container-tabs__actions {
  display: flex;
  gap: 2px;
  opacity: 0;
  transition: opacity 0.2s;
}

.container-tabs__card:hover .container-tabs__actions {
  opacity: 1;
}

.container-tabs__action {
  background: none;
  border: 1px solid transparent;
  color: #94a3b8;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  font-size: 10px;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }

  &.is-danger:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  &.is-disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }
}

.container-tabs__no {
  font-size: 12px;
  font-weight: 600;
  color: #1e293b;
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  margin-bottom: 7px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 16px;
}

.container-tabs__gauges {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}

.container-tabs__gauge-track {
  width: 100%;
  height: 3px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;
}

.container-tabs__gauge-fill {
  height: 100%;
  background: #3b82f6;
  border-radius: 2px;
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);

  &.is-weight {
    background: #10b981;
  }
}

.container-tabs__stats {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #94a3b8;
  font-weight: 500;
  line-height: 14px;
}

.container-tabs__add {
  flex: 0 0 42px;
  height: auto;
  min-height: 82px;
  background: #fff;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 18px;
  transition: all 0.2s;

  &:hover {
    border-color: #409eff;
    color: #409eff;
    background: #ecf5ff;
  }
}

.container-tabs__locked {
  font-size: 10px;
  color: #e6a23c;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  line-height: 14px;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 999px;
  padding: 0 5px;
  white-space: nowrap;
}
</style>
